import { Card, CardContent, CardHeader, Divider, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StatusSelect } from '../../../../components/atoms/status-select';
import { statusVariants } from '../../../../constans';
import { visitFlowOptions } from '../../../../utils/visit-flow-options';
import { VisitState } from '../../api/types/visits';
import { useCancelVisit } from '../../hooks/mutation/use-cancel-visit';
import { useFinishVisit } from '../../hooks/mutation/use-finish-visit';

import { LoadingButton } from '@/components/atoms/loading-button/loading-button';
import { Can } from '@/permissions/can';

interface Props {
  visitState: VisitState;
  visitId: number;
}

export const VisitStatus = ({ visitId, visitState }: Props) => {
  const { t } = useTranslation(['visit', 'common']);

  const [status, setStatus] = useState<VisitState>(visitState);

  // Sync local state with prop when visitState changes (e.g., after confirm/reject)
  useEffect(() => {
    setStatus(visitState);
  }, [visitState]);

  const quickActions = visitFlowOptions(visitState);

  const { mutate: cancelVisit, isPending: isPendingCancelVisit } = useCancelVisit(() => {
    setStatus(VisitState.CANCELLED);
  });
  const { mutate: finishVisit, isPending: isPendingFinishVisit } = useFinishVisit(() => {
    setStatus(VisitState.FINISHED);
  });

  const handleSaveChanges = () => {
    if (status === VisitState.CANCELLED) {
      cancelVisit(visitId);
    } else if (status === VisitState.FINISHED) {
      finishVisit(visitId);
    }
  };

  const disableButton = (state: VisitState) => {
    if (
      visitState === VisitState.CANCELLED ||
      visitState === VisitState.FINISHED ||
      visitState === VisitState.REJECTED
    ) {
      return true;
    } else if (state === VisitState.CANCELLED || state === VisitState.FINISHED) {
      return false;
    } else return true;
  };

  return (
    <Card variant="outlined">
      <CardHeader title={t('visit:status.cardTitle')} variant="outlined" />
      <Divider />
      <CardContent>
        <StatusSelect
          onClick={status => setStatus(status as VisitState)}
          options={quickActions}
          defaultValue={statusVariants.find(el => el.value === visitState)}
          value={status}
        />
        <Can I="update" a="Visitors" passThrough>
          {allowed => (
            <Tooltip
              title={t('common:notSufficientPermissions')}
              disableHoverListener={allowed}
              disableFocusListener={allowed}>
              <span>
                <LoadingButton
                  color="primary"
                  disabled={disableButton(status) || !allowed}
                  loading={isPendingCancelVisit || isPendingFinishVisit}
                  onClick={handleSaveChanges}
                  sx={{ mt: 2 }}
                  variant="contained">
                  {t('common:button.save')}
                </LoadingButton>
              </span>
            </Tooltip>
          )}
        </Can>
      </CardContent>
    </Card>
  );
};
